import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Flex,
  Grid,
  GridItem,
  Heading,
  Image,
  Text,
  VStack,
  Badge,
  Spinner,
  SimpleGrid,
  HStack,
  Icon,
  Divider,
  useMediaQuery,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Tag,
  TagLabel,
  Wrap,
  WrapItem,
  Code,
  Tooltip
} from '@chakra-ui/react';
import { ChevronLeftIcon, InfoIcon, CalendarIcon, StarIcon, ViewIcon } from '@chakra-ui/icons';
import supabase from '../utils/supabaseUtils';

export function EventTemplates() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLargerThan768] = useMediaQuery('(min-width: 768px)');
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  useEffect(() => {
    const fetchEventDetails = async () => {
      try {
        // Fetch event details
        const { data: eventData, error: eventError } = await supabase
          .from('events')
          .select('*')
          .eq('id', eventId)
          .single();
          
        if (eventError) throw eventError;
        
        // Parse tags
        let parsedTags = [];
        if (eventData.tags) {
          try {
            parsedTags = typeof eventData.tags === 'string'
              ? JSON.parse(eventData.tags)
              : (Array.isArray(eventData.tags) ? eventData.tags : []);
          } catch (e) {
            console.error('Error parsing tags for event:', eventData.id, e);
          }
        }
        
        setEvent({ ...eventData, tags: parsedTags });
        
        // If the event has templates, fetch them
        if (eventData.templates) {
          const templateIds = eventData.templates.split(',').filter(id => id);
          if (templateIds.length > 0) {
            const { data: templatesData, error: templatesError } = await supabase
              .from('templates')
              .select('*')
              .in('id', templateIds);
              
            if (templatesError) throw templatesError;
            setTemplates(templatesData || []);
          }
        }
      } catch (error) {
        console.error('Error fetching event details:', error);
      } finally {
        setLoading(false);
      }
    };
    
    if (eventId) {
      fetchEventDetails();
    } else {
      setLoading(false);
    }
  }, [eventId]);

  const handleTemplateClick = (template) => {
    setSelectedTemplate(template);
    onOpen();
  };

  const handleSignUp = () => {
    navigate(`/signup/${eventId}`);
  };

  // Helper function to determine if a template is PRO or FREE
  const getTemplateType = (template) => {
    // For demonstration, we'll consider templates with certain properties as PRO
    // You can adjust this logic based on your actual data model
    if (
      template.is_premium === true || 
      template.price > 0 ||
      template.style_category === 'Premium' ||
      template.name?.includes('Pro')
    ) {
      return 'PRO';
    }
    return 'FREE';
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" minH="50vh">
        <Spinner size="xl" color="blue.500" />
      </Flex>
    );
  }

  if (!event) {
    return (
      <Container maxW="container.md" py={10}>
        <Text>Event not found. Please check the URL and try again.</Text>
        <Button as={Link} to="/signup" mt={4} colorScheme="blue">
          View Available Events
        </Button>
      </Container>
    );
  }

  return (
    <Box>
      {/* Hero section with event background and logo */}
      <Box
        position="relative"
        height="200px"
        backgroundImage={event.event_image ? `url(${event.event_image})` : 'linear-gradient(to right, #4299e1, #3182ce)'}
        backgroundSize="cover"
        backgroundPosition="center"
        mb={6}
      >
        {/* Overlay for better text visibility */}
        <Box 
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          bg="blackAlpha.600"
        />
        
        {/* Event logo and title */}
        <Container maxW="container.lg" position="relative" height="100%">
          <Flex 
            height="100%" 
            alignItems="center"
            position="relative"
          >
            {event.event_image && (
              <Box
                bg="white"
                borderRadius="full"
                p="3"
                boxSize="100px"
                mr={4}
                boxShadow="md"
              >
                <Image
                  src={event.event_image}
                  alt={event.name}
                  objectFit="contain"
                  boxSize="100%"
                />
              </Box>
            )}
            
            <Box>
              <Heading color="white" size="lg" mb={2}>{event.name}</Heading>
              <HStack spacing={5} color="white" opacity={0.9}>
                <HStack>
                  <Icon as={CalendarIcon} />
                  <Text>{new Date(event.start_date).toLocaleDateString()} - {new Date(event.end_date).toLocaleDateString()}</Text>
                </HStack>
                <HStack>
                  <Icon as={InfoIcon} />
                  <Text>{event.city}, {event.state}</Text>
                </HStack>
              </HStack>
            </Box>
          </Flex>
        </Container>
      </Box>
      
      <Container maxW="container.lg" py={6}>
        <VStack spacing={8} align="stretch">
          <Box>
            <Link to={`/signup/${eventId}`}>
              <Button 
                leftIcon={<ChevronLeftIcon />} 
                variant="ghost" 
                colorScheme="blue" 
                size="sm" 
                mb={4}
              >
                Back to Event
                <Code 
                  ml={2} 
                  colorScheme="blue" 
                  fontSize="xs"
                  borderRadius="md"
                >
                  signup
                </Code>
              </Button>
            </Link>
            <Flex justify="space-between" align="center" mb={4}>
              <HStack spacing={2}>
                <Heading size="lg">Available Templates</Heading>
                <Code 
                  colorScheme="blue" 
                  children={templates.length} 
                  px={2}
                  py={1}
                  borderRadius="full"
                  fontSize="md"
                  fontWeight="bold"
                />
              </HStack>
              <Tooltip 
                label="Register for this event now" 
                placement="top"
                hasArrow
              >
                <Button 
                  colorScheme="blue" 
                  size="md" 
                  onClick={handleSignUp}
                  leftIcon={<Icon as={StarIcon} />}
                >
                  Sign Up For This Event
                </Button>
              </Tooltip>
            </Flex>
            
            <Text color="gray.600" mb={6}>
              Browse the available templates for this event. Sign up to create personalized images using these templates.
            </Text>
          </Box>
          
          {templates.length === 0 ? (
            <Box 
              textAlign="center" 
              p={10} 
              borderWidth="1px" 
              borderRadius="lg" 
              borderColor={borderColor}
              bg={bgColor}
            >
              <Heading size="md" mb={4} color="gray.600">No Templates Available Yet</Heading>
              <Text>The event organizer hasn't added any templates yet. Check back later or proceed with signup.</Text>
              <Button mt={6} colorScheme="blue" onClick={handleSignUp}>
                Sign Up Anyway
              </Button>
            </Box>
          ) : (
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
              {templates.map((template) => (
                <Box
                  key={template.id}
                  borderWidth="1px"
                  borderRadius="lg"
                  overflow="hidden"
                  bg={bgColor}
                  boxShadow="md"
                  transition="all 0.3s"
                  _hover={{ transform: 'translateY(-5px)', boxShadow: 'lg' }}
                  cursor="pointer"
                  onClick={() => handleTemplateClick(template)}
                >
                  <Box position="relative" height="200px">
                    <Image
                      src={template.thumbnail_url || "https://via.placeholder.com/400x300?text=No+Preview"}
                      alt={template.name}
                      width="100%"
                      height="100%"
                      objectFit="cover"
                    />
                    <Flex
                      position="absolute"
                      bottom="0"
                      left="0"
                      right="0"
                      bg="blackAlpha.700"
                      color="white"
                      p={2}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Icon as={ViewIcon} mr={2} />
                      <Text fontWeight="medium">Preview Template</Text>
                    </Flex>
                    
                    {/* Modern badge indicator in top-right corner */}
                    <Box
                      position="absolute"
                      top="10px"
                      right="10px"
                      bg={getTemplateType(template) === 'PRO' ? 'orange.500' : 'green.500'}
                      color="white"
                      borderRadius="full"
                      boxShadow="lg"
                      overflow="hidden"
                    >
                      <Code
                        bg="transparent"
                        p={1}
                        px={3}
                        fontSize="xs"
                        fontWeight="bold"
                        letterSpacing="wide"
                        textTransform="uppercase"
                      >
                        {getTemplateType(template)}
                      </Code>
                    </Box>
                  </Box>
                  
                  <Box p={4}>
                    <Heading size="md" mb={2} noOfLines={1}>
                      {template.name || "Untitled Template"}
                    </Heading>
                    
                    {template.description && (
                      <Text color="gray.600" noOfLines={2} mb={3}>
                        {template.description}
                      </Text>
                    )}
                    
                    {template.style_category && (
                      <Wrap spacing={2} mt={2}>
                        <WrapItem>
                          <Tag size="sm" colorScheme="blue" borderRadius="full">
                            <TagLabel>{template.style_category}</TagLabel>
                          </Tag>
                        </WrapItem>
                        {template.style_subcategory && (
                          <WrapItem>
                            <Tag size="sm" colorScheme="purple" borderRadius="full">
                              <TagLabel>{template.style_subcategory}</TagLabel>
                            </Tag>
                          </WrapItem>
                        )}
                      </Wrap>
                    )}
                  </Box>
                </Box>
              ))}
            </SimpleGrid>
          )}
          
          <Divider my={4} />
          
          <Box textAlign="center" py={4}>
            <Button
              size="lg"
              colorScheme="blue"
              onClick={handleSignUp}
              px={8}
              py={6}
              rightIcon={
                <Code
                  colorScheme="blue"
                  bg="blue.700"
                  color="white"
                  px={2}
                  py={1}
                  borderRadius="md"
                  fontSize="xs"
                  letterSpacing="wide"
                >
                  REGISTER NOW
                </Code>
              }
            >
              Sign Up For {event.name}
            </Button>
            <Text mt={4} color="gray.600">
              Create your own custom images by signing up for this event.
            </Text>
          </Box>
        </VStack>
      </Container>
      
      {/* Template Preview Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent borderRadius="md">
          <ModalHeader>
            <Flex justifyContent="space-between" alignItems="center">
              <Text>{selectedTemplate?.name || "Template Preview"}</Text>
              {selectedTemplate && (
                <Code
                  colorScheme={getTemplateType(selectedTemplate) === 'PRO' ? 'orange' : 'green'}
                  px={3}
                  py={1}
                  borderRadius="full"
                  fontSize="xs"
                  fontWeight="bold"
                  textTransform="uppercase"
                >
                  {getTemplateType(selectedTemplate)}
                </Code>
              )}
            </Flex>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedTemplate && (
              <VStack spacing={4} align="stretch">
                <Box borderRadius="md" overflow="hidden">
                  <Image
                    src={selectedTemplate.thumbnail_url || "https://via.placeholder.com/800x600?text=No+Preview"}
                    alt={selectedTemplate.name}
                    width="100%"
                  />
                </Box>
                
                {selectedTemplate.description && (
                  <Text color="gray.700">{selectedTemplate.description}</Text>
                )}
                
                <Box bg="gray.50" p={4} borderRadius="md">
                  <Text fontWeight="medium" mb={2}>Template Details</Text>
                  
                  <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                    <GridItem>
                      <Text fontSize="sm" color="gray.500">Type</Text>
                      <Code 
                        colorScheme={getTemplateType(selectedTemplate) === 'PRO' ? 'orange' : 'green'} 
                        borderRadius="md" 
                        px={2} 
                        py={1}
                      >
                        {getTemplateType(selectedTemplate)}
                      </Code>
                    </GridItem>
                    
                    {selectedTemplate.style_category && (
                      <GridItem>
                        <Text fontSize="sm" color="gray.500">Category</Text>
                        <Code colorScheme="blue" borderRadius="md" px={2} py={1}>
                          {selectedTemplate.style_category}
                        </Code>
                      </GridItem>
                    )}
                    
                    {selectedTemplate.style_subcategory && (
                      <GridItem>
                        <Text fontSize="sm" color="gray.500">Style</Text>
                        <Code colorScheme="purple" borderRadius="md" px={2} py={1}>
                          {selectedTemplate.style_subcategory}
                        </Code>
                      </GridItem>
                    )}
                    
                    {selectedTemplate.created_at && (
                      <GridItem>
                        <Text fontSize="sm" color="gray.500">Created</Text>
                        <Code colorScheme="gray" borderRadius="md" px={2} py={1}>
                          {new Date(selectedTemplate.created_at).toLocaleDateString()}
                        </Code>
                      </GridItem>
                    )}
                  </Grid>
                </Box>
                
                <Button
                  mt={4}
                  colorScheme="blue"
                  onClick={handleSignUp}
                  size="lg"
                  width="full"
                >
                  Sign Up To Use This Template
                </Button>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
} 